import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MangaCard from '@/components/feature/MangaCard';
import { categories, mangas } from '@/mocks/mangas';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

type SortKey = 'update' | 'hot' | 'rating' | 'newest';
type StatusFilter = 'all' | '连载中' | '已完结';

export default function CategoryPage() {
  const { slug = 'all' } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const statusParam = searchParams.get('status') || '';

  const [sort, setSort] = useState<SortKey>('update');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    statusParam === 'complete' ? '已完结' : 'all',
  );

  const category = categories.find((c) => c.slug === slug);
  const isNew = slug === 'new';

  const titleName = isNew ? '日漫新番' : category ? category.name : '全部漫画';
  const title = q ? `搜索“${q}”` : titleName;

  useDocumentMeta(
    `樱花漫画网${titleName}在线阅读 - 少女漫画·恋爱漫画·唯美漫画`,
    `樱花漫画网${titleName}专区，分类清晰，点开即读。聚合少女漫画、唯美漫画、校园恋爱与耽美漫画，热门日漫新番同步更新，收藏追更更方便。`,
  );

  const filtered = useMemo(() => {
    let list = [...mangas];
    if (isNew) list = list.filter((m) => m.category === 'new');
    else if (category) list = list.filter((m) => m.category === slug);
    if (statusFilter !== 'all') list = list.filter((m) => m.status === statusFilter);
    if (q) {
      const k = q.toLowerCase();
      list = list.filter(
        (m) => m.title.includes(q) || m.tags.some((t) => t.includes(q)) || m.author.includes(q) || m.title.toLowerCase().includes(k),
      );
    }
    switch (sort) {
      case 'update':
        list.sort((a, b) => (a.updateTime > b.updateTime ? -1 : 1));
        break;
      case 'hot':
        list.sort((a, b) => Number(b.views.replace(/[^0-9]/g, '')) - Number(a.views.replace(/[^0-9]/g, '')));
        break;
      case 'rating':
        list.sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        list.sort((a, b) => Number(b.year) - Number(a.year));
        break;
    }
    return list;
  }, [slug, q, sort, statusFilter, isNew, category]);

  const sortOptions: { key: SortKey; label: string }[] = [
    { key: 'update', label: '更新优先' },
    { key: 'hot', label: '热度最高' },
    { key: 'rating', label: '评分最高' },
    { key: 'newest', label: '新作首发' },
  ];
  const statusOptions: StatusFilter[] = ['all', '连载中', '已完结'];

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-[72px]">
        {/* 分类横幅 */}
        <section className="bg-gradient-to-r from-primary-500 to-primary-600 py-10 md:py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <nav aria-label="面包屑" className="text-xs text-primary-50/80">
              <ol className="flex items-center gap-1.5">
                <li><Link to="/" className="hover:underline">首页</Link></li>
                <li>/</li>
                <li><Link to="/category/all" className="hover:underline">漫画</Link></li>
                <li>/</li>
                <li aria-current="page" className="text-background-50">{q ? `搜索：${q}` : titleName}</li>
              </ol>
            </nav>
            <div className="mt-4 flex items-center gap-4">
              <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-background-50/20 text-background-50">
                <i className={`${isNew ? 'ri-flashlight-fill' : category ? category.icon : 'ri-book-open-fill'} text-2xl`}></i>
              </span>
              <div>
                <h1 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
                  {q ? `“${q}” 的搜索结果` : titleName}
                </h1>
                <p className="mt-1 text-sm text-primary-50">
                  {q ? `共找到 ${filtered.length} 部相关漫画` : `${category?.desc || '聚合少女漫画、恋爱漫画、唯美漫画与耽美漫画，分类清晰，点开即读。'} · 共 ${filtered.length} 部`}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 筛选区 */}
        <section className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            {/* 排序 */}
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1" role="group" aria-label="排序方式">
              {sortOptions.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setSort(o.key)}
                  className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition ${
                    sort === o.key ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {/* 状态 */}
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1" role="group" aria-label="连载状态">
              {statusOptions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition ${
                    statusFilter === s ? 'bg-secondary-500 text-background-50' : 'text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {s === 'all' ? '全部状态' : s}
                </button>
              ))}
            </div>
          </div>

          {/* 卡片列表 */}
          {filtered.length > 0 ? (
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
              {filtered.map((m) => (
                <MangaCard key={m.id} manga={m} />
              ))}
            </div>
          ) : (
            <div className="mt-16 flex flex-col items-center justify-center py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-300">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </span>
              <p className="mt-4 font-semibold text-foreground-800">没有找到匹配的漫画</p>
              <p className="mt-1 text-sm text-foreground-500">换个关键词或筛选条件试试吧</p>
              <Link
                to="/category/all"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition hover:bg-primary-600"
              >
                <i className="ri-refresh-line"></i>
                浏览全部漫画
              </Link>
            </div>
          )}
        </section>

        {/* 分类入口 */}
        <section className="mx-auto max-w-7xl px-4 pb-12 md:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500">热门分类：</span>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="rounded-full bg-background-100 px-4 py-1.5 text-sm text-foreground-600 transition hover:bg-primary-100 hover:text-primary-700"
              >
                {c.name}
              </Link>
            ))}
            <Link
              to="/category/new"
              className="rounded-full bg-background-100 px-4 py-1.5 text-sm text-foreground-600 transition hover:bg-primary-100 hover:text-primary-700"
            >
              日漫新番
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}